import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQueries } from "@tanstack/react-query";
import {
  Check,
  Heart,
  LogOut,
  Mail,
  Pen,
  Phone,
  ShieldCheck,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { useWishlist } from "@/lib/wishlist-context";
import { authService, listingService } from "@/lib/services";
import { PageShell } from "@/components/nav";
import { ListingCard } from "@/components/listing-card";
import { formatDate } from "@/lib/format";
import type { Listing } from "@/types/api";
import { extractErrorMessage } from "@/lib/api";
import { uploadAvatar, validateImageFile } from "@/lib/upload";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate({ to: "/" });
  };

  if (!user) return null;

  const displayAvatar = avatarPreview ?? user.avatar;

  const onPickAvatar = () => inputRef.current?.click();

  /*
    Upload the file, then save the URL it returns. The previous version
    base64-encoded the image into the `avatar` column itself, which meant every
    response carrying a user also carried the whole picture.
  */
  const onSelectAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;

    const invalid = validateImageFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }

    // Show the local file immediately; the round trip only confirms it.
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);
    setSavingAvatar(true);

    try {
      const stored = await uploadAvatar(file);
      await authService.updateProfile({ avatar: stored.url });
      await refresh();
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Couldn't update profile photo"));
      setAvatarPreview(null);
    } finally {
      URL.revokeObjectURL(localUrl);
      setSavingAvatar(false);
    }
  };

  const onRemoveAvatar = async () => {
    setSavingAvatar(true);
    try {
      await authService.removeAvatar();
      await refresh();
      setAvatarPreview(null);
      setViewerOpen(false);
      toast.success("Profile photo removed.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Couldn't remove profile photo"));
    } finally {
      setSavingAvatar(false);
    }
  };

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl space-y-8 px-4 py-10">
        <div className="rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-card)]">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setViewerOpen(true)}
              className="relative block h-16 w-16 overflow-hidden rounded-full bg-primary text-primary-foreground"
              aria-label="View and edit profile photo"
            >
              {displayAvatar ? (
                <img
                  src={displayAvatar}
                  alt={`${user.name} avatar`}
                  className="h-full w-full object-cover"
                  onError={() => setAvatarPreview(null)}
                />
              ) : (
                <span className="grid h-full w-full place-items-center text-2xl font-extrabold">
                  {user.name?.[0]?.toUpperCase() ?? "U"}
                </span>
              )}
              <span className="absolute bottom-0 right-0 grid h-6 w-6 place-items-center rounded-full border border-card bg-primary text-primary-foreground shadow-[var(--shadow-card)]">
                <Pen className="h-3.5 w-3.5" />
              </span>
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onSelectAvatar}
            />
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">{user.name}</h1>
              <p className="text-sm capitalize text-ink-muted">{user.role}</p>
              {savingAvatar && <p className="mt-1 text-xs text-primary">Saving photo…</p>}
            </div>
          </div>

          {editing ? (
            <ProfileForm
              initialName={user.name}
              initialPhone={user.phone ?? ""}
              onCancel={() => setEditing(false)}
              onSaved={() => setEditing(false)}
            />
          ) : (
            <dl className="mt-8 grid gap-3">
              <Row icon={Mail} label="Email" value={user.email} />
              <Row icon={Phone} label="Phone" value={user.phone || "Not added"} />
              <Row icon={UserIcon} label="Member since" value={formatDate(user.created_at)} />
              {user.role === "host" && (
                <Row
                  icon={ShieldCheck}
                  label="Host dashboard"
                  value={user.can_access_host_dashboard ? "Enabled" : "Pending verification"}
                />
              )}
            </dl>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-bold text-ink hover:bg-muted"
              >
                <Pen className="h-4 w-4" /> Edit profile
              </button>
            )}
            <Link
              to="/bookings"
              className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-bold text-ink hover:bg-muted"
            >
              My bookings
            </Link>
            <Link
              to="/chats"
              className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-bold text-ink hover:bg-muted"
            >
              My messages
            </Link>
            <button
              onClick={onLogout}
              className="ml-auto inline-flex items-center gap-2 rounded-xl border border-destructive/40 px-4 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/5"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        <WishlistSection />
      </div>

      {viewerOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 p-4">
          <button
            type="button"
            className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70"
            onClick={() => setViewerOpen(false)}
            aria-label="Close image viewer"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex h-full flex-col items-center justify-center gap-6">
            <div className="max-h-[75vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-white/25 bg-black/40">
              {displayAvatar ? (
                <img
                  src={displayAvatar}
                  alt={`${user.name} profile photo`}
                  className="h-full max-h-[75vh] w-full object-contain"
                  onError={() => setAvatarPreview(null)}
                />
              ) : (
                <div className="grid h-[60vh] w-full place-items-center text-white/80">
                  <div className="grid h-28 w-28 place-items-center rounded-full bg-primary text-5xl font-extrabold text-primary-foreground">
                    {user.name?.[0]?.toUpperCase() ?? "U"}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={onPickAvatar}
                disabled={savingAvatar}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow-[var(--shadow-elevated)] hover:opacity-90 disabled:opacity-60"
              >
                <Pen className="h-4 w-4" />
                {savingAvatar ? "Updating…" : "Edit photo"}
              </button>
              {displayAvatar && (
                <button
                  type="button"
                  onClick={onRemoveAvatar}
                  disabled={savingAvatar}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10 disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

/**
 * Name and phone, the two things a guest can actually change about themselves.
 *
 * Email and role are deliberately absent: changing an email is an identity
 * change that needs re-verification, and role is decided by the host upgrade
 * flow. The server enforces both regardless of what this form sends.
 */
function ProfileForm({
  initialName,
  initialPhone,
  onCancel,
  onSaved,
}: {
  initialName: string;
  initialPhone: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);

  const save = useMutation({
    mutationFn: () =>
      authService.updateProfile({ name: name.trim(), phone: phone.trim() || undefined }),
    onSuccess: async () => {
      await refresh();
      toast.success("Profile updated.");
      onSaved();
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Couldn't update your profile")),
  });

  const dirty = name.trim() !== initialName || phone.trim() !== initialPhone;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (name.trim().length < 2) {
      toast.error("Your name needs at least two characters.");
      return;
    }
    save.mutate();
  };

  return (
    <form onSubmit={submit} className="mt-8 space-y-4">
      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
          Name
        </span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="name"
          className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-ink outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
          Phone
        </span>
        <input
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          inputMode="tel"
          autoComplete="tel"
          placeholder="+256 700 000 000"
          className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-ink outline-none placeholder:text-ink-subtle focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
        />
        <span className="mt-1 block text-[11px] text-ink-subtle">
          Hosts use this to reach you about a stay.
        </span>
      </label>

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={save.isPending || !dirty}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-bold text-ink-muted transition hover:bg-muted hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function WishlistSection() {
  const { ids, clear } = useWishlist();

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["listing", id],
      queryFn: () => listingService.get(id),
      staleTime: 60_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const listings: Listing[] = queries
    .map((q) => q.data?.data)
    .filter((x): x is Listing => Boolean(x));

  return (
    <section className="rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-ink">
            <Heart className="h-5 w-5 fill-destructive text-destructive" />
            Wishlist
          </h2>
          <p className="mt-1 text-sm text-ink-muted">
            {ids.length === 0
              ? "Tap the heart on any listing to save it for later."
              : `${ids.length} saved listing${ids.length === 1 ? "" : "s"}.`}
          </p>
        </div>
        {ids.length > 0 && (
          <button
            type="button"
            onClick={() => {
              clear();
              toast.success("Wishlist cleared");
            }}
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-bold text-ink-muted hover:bg-muted hover:text-ink"
          >
            Clear all
          </button>
        )}
      </header>

      {ids.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-background p-8 text-center text-sm text-ink-muted">
          Nothing saved yet.{" "}
          <Link to="/" className="font-bold text-primary hover:underline">
            Browse stays →
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {isLoading && listings.length === 0
            ? Array.from({ length: Math.min(ids.length, 2) }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="aspect-[4/3] animate-pulse brand-skeleton" />
                  <div className="space-y-2 p-4">
                    <div className="h-3 w-1/3 animate-pulse rounded brand-skeleton" />
                    <div className="h-4 w-2/3 animate-pulse rounded brand-skeleton" />
                  </div>
                </div>
              ))
            : listings.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}
    </section>
  );
}

function Row({ icon: Icon, label, value }: { icon: typeof Mail; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4">
      <Icon className="h-4 w-4 text-ink-muted" />
      <div className="flex-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-ink-subtle">{label}</p>
        <p className="text-sm font-semibold text-ink">{value}</p>
      </div>
    </div>
  );
}
